import { withAuthenticationRequired } from "@auth0/auth0-react";
import { useAuth0 } from "@auth0/auth0-react";
import { CONFIG as config } from "../config";
import { InputAdornment, MenuItem, TextField } from "@mui/material";
import { Grid, Paper, Box, Typography, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { Controller, useForm } from "react-hook-form";
import { postData } from "../API/Post";
import updateSnackbarMessage from "../functions/updateSnackbarMessage";
import { Button } from "@mui/material";
import { Stack } from "@mui/material";
import { putData } from "../API/Put";
import fetchEstablishments from "../functions/fetchEstablishments";
import fetchData from "../API/Fetch";
import {
  Store,
  Euro,
  Settings,
  Tv,
  WaterDrop,
  Bolt,
  Phone,
  Save,
  Cancel,
  Category,
} from "@mui/icons-material";
import { deleteData } from "../API/Delete";
import fetchZones from "../functions/fetchZones";

const brandManagedFields = [
  "yellow_water_limit_per_person",
  "yellow_elec_limit_per_person",
  "red_water_limit_per_person",
  "red_elec_limit_per_person",
  "screen_brightness",
  "uplink_rate_seconds",
  "screen_on_start",
  "screen_on_end",
  "cost_per_liter",
  "cost_per_kwh",
  "day_start_hour",
];

function EstablishmentUpdate() {
  const brands = useSelector((state) => state.brands);
  const params = useParams();
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const userDB = useSelector((state) => state.userDB);
  const isAdmin = userDB && userDB.admin;

  const [initialEstablishment, setInitialEstablishment] = useState(null);
  const [effectiveDefaults, setEffectiveDefaults] = useState({});
  const [formInitialized, setFormInitialized] = useState(false);
  const [smsContacts, setSmsContacts] = useState([]);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const { getAccessTokenSilently } = useAuth0();

  // États pour la gestion des zones
  const [establishmentZones, setEstablishmentZones] = useState([]);
  const [newZoneName, setNewZoneName] = useState("");
  const [newZoneColor, setNewZoneColor] = useState("#c4a574");
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [editZoneName, setEditZoneName] = useState("");
  const [editZoneColor, setEditZoneColor] = useState("");

  const { handleSubmit, control, reset, watch } = useForm({
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      brand_id: "",
      stars: 0,
      cost_per_liter: 2.5,
      cost_per_kwh: 0.2526,
      is_zone: false,
      show_hide_screen: true,
      show_hide_occupancy: true,
      yellow_water_limit_per_person: null,
      yellow_elec_limit_per_person: null,
      red_water_limit_per_person: null,
      red_elec_limit_per_person: null,
      screen_brightness: null,
      uplink_rate_seconds: null,
      screen_on_start: null,
      screen_on_end: null,
      day_start_hour: 0,
    },
  });

  const isZone = watch("is_zone");
  const selectedBrandId = watch("brand_id");

  useEffect(() => {
    if (selectedBrandId && brands.length > 0) {
      const brand = brands.find((b) => b.id === selectedBrandId);
      if (brand) {
        const defaults = {};
        brandManagedFields.forEach((field) => {
          defaults[field] = brand[field] ?? "";
        });
        setEffectiveDefaults(defaults);
      }
    }
  }, [selectedBrandId, brands]);

  useEffect(() => {
    store.dispatch({
      type: "pageActions/update",
      payload: [],
    });

    if (!params.id) {
      store.dispatch({
        type: "pageTitle/update",
        payload: t("common.add_new", { name: t("common.establishment") }),
      });
      return;
    }

    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.update") + " " + t("common.establishment"),
    });

    fetchData(`/establishments/${params.id}`)
      .then((data) => {
        if (data) {
          setInitialEstablishment(data);
        } else {
          updateSnackbarMessage(
            t("common.fetch_error", { name: t("common.establishment") })
          );
          navigate("/establishments/");
        }
      })
      .catch((error) => {
        updateSnackbarMessage(
          t("common.fetch_error", { name: t("common.establishment") }) +
            `: ${error.message}`
        );
        navigate("/establishments/");
      });
  }, [params.id, t, navigate]);

  // Charger la configuration SMS
  useEffect(() => {
    if (!params.id) return;

    const loadAlertConfig = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${config.API_URL}/api/alert-configs/establishment/${params.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSmsContacts(data.sms_contacts || []);
          setSmsEnabled(data.enabled !== false);
        }
      } catch (error) {
        // Erreur silencieuse
      }
    };

    loadAlertConfig();
  }, [params.id, getAccessTokenSilently]);

  // Charger les zones de l'établissement
  useEffect(() => {
    const loadEstablishmentZones = async () => {
      if (params.id) {
        try {
          const zones = await fetchData(`/zones/establishment/${params.id}`);
          setEstablishmentZones(zones || []);
        } catch (error) {
          // Erreur silencieuse
        }
      }
    };
    loadEstablishmentZones();
  }, [params.id]);

  // Ajouter une zone
  const handleAddZone = async () => {
    if (!newZoneName.trim() || !params.id) return;

    setIsAddingZone(true);
    try {
      const zoneData = {
        name: newZoneName.trim(),
        establishment_id: parseInt(params.id),
        color: newZoneColor,
        show_on_dashboard: true,
      };

      const response = await postData("/zones/", zoneData);

      if (response && (response.status === 200 || response.status === 201 || response.data)) {
        updateSnackbarMessage(t("common.added_success", { name: t("common.zone") }));
        setNewZoneName("");
        // Recharger les zones
        const zones = await fetchData(`/zones/establishment/${params.id}`);
        setEstablishmentZones(zones || []);
        // Mettre à jour le store global
        fetchZones();
      } else {
        const errorMessage = response?.response?.data?.detail ||
                            response?.message ||
                            t("common.add_error", { name: t("common.zone") });
        updateSnackbarMessage(errorMessage);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.detail ||
                          error?.message ||
                          t("common.add_error", { name: t("common.zone") });
      updateSnackbarMessage(errorMessage);
    } finally {
      setIsAddingZone(false);
    }
  };

  // Supprimer une zone
  const handleDeleteZone = async (zoneId) => {
    try {
      const response = await deleteData(`/zones/${zoneId}`);
      if (response && (response.status === 200 || response.status === 204)) {
        updateSnackbarMessage(t("common.delete_success", { name: t("common.zone") }));
        // Recharger les zones
        const zones = await fetchData(`/zones/establishment/${params.id}`);
        setEstablishmentZones(zones || []);
        // Mettre à jour le store global
        fetchZones();
      } else {
        updateSnackbarMessage("Erreur lors de la suppression de la zone");
      }
    } catch (error) {
      updateSnackbarMessage("Erreur lors de la suppression de la zone");
    }
  };

  // Modifier une zone
  const handleStartEditZone = (zone) => {
    setEditingZone(zone);
    setEditZoneName(zone.name);
    setEditZoneColor(zone.color || "#c4a574");
  };

  const handleSaveEditZone = async () => {
    if (!editZoneName.trim() || !editingZone) return;
    try {
      const response = await putData(`/zones/${editingZone.id}`, {
        name: editZoneName.trim(),
        color: editZoneColor,
      });
      if (response && (response.status === 200 || response.status === 201 || response.data)) {
        updateSnackbarMessage(t("common.update_success", { name: t("common.zone") }));
        const zones = await fetchData(`/zones/establishment/${params.id}`);
        setEstablishmentZones(zones || []);
        setEditingZone(null);
        fetchZones();
      }
    } catch (error) {
      updateSnackbarMessage("Erreur lors de la modification de la zone");
    }
  };

  const handleCancelEditZone = () => {
    setEditingZone(null);
  };

  useEffect(() => {
    if (!initialEstablishment || brands.length === 0 || formInitialized) {
      return;
    }

    const establishmentBrand = brands.find(
      (b) => b.id === initialEstablishment.brand_id
    );

    const brandDefaults = {};
    if (establishmentBrand) {
      brandManagedFields.forEach((field) => {
        brandDefaults[field] = establishmentBrand[field] ?? "";
      });
    }

    const resetValues = {
      name: initialEstablishment.name,
      brand_id: initialEstablishment.brand_id,
      stars: initialEstablishment.stars,
      show_hide_screen: initialEstablishment.show_hide_screen,
      show_hide_occupancy: initialEstablishment.show_hide_occupancy,
      is_zone: initialEstablishment.is_zone,
      zone_name: initialEstablishment.zone_name || "",
    };

    brandManagedFields.forEach((field) => {
      resetValues[field] =
        initialEstablishment[field] !== null &&
        initialEstablishment[field] !== undefined
          ? initialEstablishment[field]
          : brandDefaults[field] ?? "";
    });

    reset(resetValues);
    setFormInitialized(true);
  }, [initialEstablishment, reset, brands, formInitialized]);

  const getPlaceholder = useCallback(
    (fieldName) => {
      const value = effectiveDefaults[fieldName];
      if (value !== null && value !== undefined && value !== "") {
        if (fieldName.includes("_water_")) return `${value} Liters`;
        if (fieldName.includes("_elec_")) return `${value} kWh`;
        if (fieldName.includes("cost_per_liter")) return `${value} €/Liter`;
        if (fieldName.includes("cost_per_kwh")) return `${value} €/kWh`;
        if (fieldName.includes("screen_brightness")) return `${value} %`;
        if (fieldName.includes("uplink_rate_seconds")) return `${value} s`;
        return value.toString();
      }
      return "";
    },
    [effectiveDefaults]
  );

  const validationLabels = {
    name: {
      required: t("common.required", { name: t("common.name") }),
    },
    brand: {
      required: t("common.required", { name: t("common.brand") }),
    },
    stars: {
      required: t("common.required", { name: t("common.stars") }),
      min: t("common.min") + " 0",
      max: t("common.max") + " 5",
    },
    yellow_water_limit_per_person: {
      number: t("common.number_only"),
    },
    yellow_elec_limit_per_person: {
      number: t("common.number_only"),
    },
    red_water_limit_per_person: {
      number: t("common.number_only"),
    },
    red_elec_limit_per_person: {
      number: t("common.number_only"),
    },
    screen_brightness: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 100",
    },
    uplink_rate_seconds: {
      number: t("common.number_only"),
    },
    screen_on_start: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 23",
    },
    screen_on_end: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 23",
    },
    cost_per_liter: {
      number: t("common.number_only"),
    },
    cost_per_kwh: {
      number: t("common.number_only"),
    },
    zone_name: {
      required: t("common.required", { name: t("common.zone_name") }),
    },
    day_start_hour: {
      number: t("common.number_only"),
      min: t("common.min") + " 0",
      max: t("common.max") + " 23",
    },
  };

  const onSubmit = async (data) => {
    // Sauvegarder la config SMS pour les updates
    if (params.id) {
      const smsSuccess = await saveSmsConfig();
      if (!smsSuccess) {
        return; // Arrêter si erreur SMS
      }
    }

    // Required fields
    let jsonData = {
      name: data.name,
      brand_id: parseInt(data.brand_id, 10),
      stars: parseInt(data.stars, 10),
    };

    // Optional boolean fields (must be boolean, not string)
    if (typeof data.show_hide_screen !== "undefined") {
      jsonData.show_hide_screen = Boolean(
        data.show_hide_screen === true ||
          data.show_hide_screen === "true" ||
          data.show_hide_screen === 1 ||
          data.show_hide_screen === "1"
      );
    }
    if (typeof data.show_hide_occupancy !== "undefined") {
      jsonData.show_hide_occupancy = Boolean(
        data.show_hide_occupancy === true ||
          data.show_hide_occupancy === "true" ||
          data.show_hide_occupancy === 1 ||
          data.show_hide_occupancy === "1"
      );
    }
    if (typeof data.is_zone !== "undefined") {
      jsonData.is_zone = Boolean(
        data.is_zone === true ||
          data.is_zone === "true" ||
          data.is_zone === 1 ||
          data.is_zone === "1"
      );
    }
    if (jsonData.is_zone && data.zone_name) {
      jsonData.zone_name = data.zone_name;
    }

    // Only include numeric/optional fields if they are not empty/null/undefined
    brandManagedFields.forEach((field) => {
      if (field === "uplink_rate_seconds" && userDB && userDB.admin) {
        // handled below
        return;
      }
      const formValue = data[field];
      if (formValue !== null && formValue !== undefined && formValue !== "") {
        // All these fields are numbers (int or float)
        jsonData[field] = Number(formValue);
      }
    });

    // Only admins can set uplink_rate_seconds
    if (
      userDB &&
      userDB.admin &&
      data.uplink_rate_seconds !== null &&
      data.uplink_rate_seconds !== undefined &&
      data.uplink_rate_seconds !== ""
    ) {
      jsonData.uplink_rate_seconds = Number(data.uplink_rate_seconds);
    }

    try {
      let response = null;

      if (initialEstablishment) {
        response = await putData(
          `/establishments/${initialEstablishment.id}`,
          jsonData
        );
      } else {
        response = await postData(`/establishments/`, jsonData);
      }

      if (response && response.status === 200) {
        updateSnackbarMessage(
          initialEstablishment === null
            ? t("common.added_success", { name: t("common.establishment") })
            : t("common.updated_success", { name: t("common.establishment") })
        );

        fetchEstablishments();
        navigate("/establishments/");
      } else {
        const errorDetail = response?.data?.detail || "Unknown error";
        updateSnackbarMessage(
          initialEstablishment === null
            ? t("common.add_error", { name: t("common.establishment") }) +
                `: ${errorDetail}`
            : t("common.update_error", { name: t("common.establishment") }) +
                `: ${errorDetail}`
        );
      }
    } catch (error) {
      const errorDetail =
        error?.response?.data?.detail || error.message || "Unknown error";
      updateSnackbarMessage(
        initialEstablishment === null
          ? t("common.add_error", { name: t("common.establishment") }) +
              `: ${errorDetail}`
          : t("common.update_error", { name: t("common.establishment") }) +
              `: ${errorDetail}`
      );
    }
  };


  const handleAddSmsContact = () => {
    setSmsContacts([...smsContacts, { name: "", phone: "" }]);
  };

  const handleRemoveSmsContact = (index) => {
    setSmsContacts(smsContacts.filter((_, i) => i !== index));
  };

  const handleSmsContactChange = (index, field, value) => {
    const updated = [...smsContacts];
    updated[index][field] = value;
    setSmsContacts(updated);
  };

  const saveSmsConfig = async () => {
    try {
      const token = await getAccessTokenSilently();

      for (const contact of smsContacts) {
        if (contact.name && contact.phone && !contact.phone.startsWith('+')) {
          updateSnackbarMessage(
            store,
            "error",
            `Le numéro de ${contact.name} doit être au format international (+33...)`
          );
          return false;
        }
      }

      const response = await fetch(
        `${config.API_URL}/api/alert-configs/establishment/${params.id || initialEstablishment.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            sms_contacts: smsContacts.filter(c => c.name && c.phone),
            enabled: smsEnabled,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de la sauvegarde");
      }

      return true;
    } catch (error) {
      updateSnackbarMessage(store, "error", error.message);
      return false;
    }
  };

  // Style commun pour les sections
  const sectionStyle = {
    p: 3,
    mb: 2,
    borderRadius: 2,
  };

  return (
    <Grid container spacing={2}>
      {/* Section Informations générales */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #78a882` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Store sx={{ color: "#78a882" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {t("common.information")}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="name"
                    label={t("common.name")}
                    fullWidth
                    {...field}
                    disabled={!isAdmin}
                    error={error !== undefined}
                    helperText={error ? validationLabels.name[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="stars"
                control={control}
                rules={{ required: true, min: 0, max: 5 }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="stars"
                    label={t("common.stars")}
                    fullWidth
                    {...field}
                    disabled={!isAdmin}
                    type="number"
                    error={error !== undefined}
                    helperText={error ? validationLabels.stars[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="brand_id"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="brand_id"
                    label={t("common.brand")}
                    fullWidth
                    select
                    {...field}
                    disabled={!isAdmin}
                    error={error !== undefined}
                    helperText={error ? validationLabels.brand[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  >
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Section Zones */}
      {params.id && (
        <Grid item xs={12}>
          <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #c4a574` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Category sx={{ color: "#c4a574" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {t("common.zones") || "Zones"}
              </Typography>
              <Chip
                label={establishmentZones.length}
                size="small"
                sx={{ ml: 1, backgroundColor: "#c4a574", color: "white" }}
              />
            </Box>

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Définissez les zones/catégories disponibles pour cet établissement.
              Les installations pourront ensuite être assignées à ces zones pour faciliter le filtrage sur le tableau de bord.
            </Typography>

            {/* Liste des zones existantes */}
            {establishmentZones.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {establishmentZones.map((zone) => (
                    <Box key={zone.id} sx={{ display: "inline-flex", alignItems: "center", mb: 1 }}>
                      <Chip
                        label={zone.name}
                        onClick={() => handleStartEditZone(zone)}
                        onDelete={() => handleDeleteZone(zone.id)}
                        deleteIcon={<DeleteIcon />}
                        sx={{
                          backgroundColor: editingZone?.id === zone.id
                            ? alpha(zone.color || "#c4a574", 0.7)
                            : zone.color || "#c4a574",
                          color: "white",
                          cursor: "pointer",
                          "& .MuiChip-label:hover": {
                            textDecoration: "underline",
                          },
                          "& .MuiChip-deleteIcon": {
                            color: "rgba(255,255,255,0.7)",
                            "&:hover": { color: "white" },
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Formulaire d'édition de zone */}
            {editingZone && (
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap", mb: 2, p: 2, borderRadius: 1, backgroundColor: alpha("#c4a574", 0.1) }}>
                <TextField
                  label="Modifier le nom"
                  value={editZoneName}
                  onChange={(e) => setEditZoneName(e.target.value)}
                  size="small"
                  sx={{ minWidth: 250, flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveEditZone();
                    }
                  }}
                />
                <TextField
                  label="Couleur"
                  type="color"
                  value={editZoneColor}
                  onChange={(e) => setEditZoneColor(e.target.value)}
                  size="small"
                  sx={{ width: 100 }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveEditZone}
                  disabled={!editZoneName.trim()}
                  sx={{
                    backgroundColor: "#c4a574",
                    "&:hover": { backgroundColor: "#b39564" },
                    height: 40,
                  }}
                >
                  {t("common.update") || "Modifier"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancelEditZone}
                  sx={{ height: 40 }}
                >
                  {t("common.cancel") || "Annuler"}
                </Button>
              </Box>
            )}

            {/* Formulaire d'ajout de zone */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap" }}>
              <TextField
                label={t("common.zone_name") || "Nom de la zone"}
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                placeholder={t("common.zone_name_placeholder") || "Ex: Mobil-homes, Compteurs généraux..."}
                size="small"
                sx={{ minWidth: 250, flex: 1 }}
                InputLabelProps={{ shrink: true }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddZone();
                  }
                }}
              />
              <TextField
                label={t("common.zone_color") || "Couleur"}
                type="color"
                value={newZoneColor}
                onChange={(e) => setNewZoneColor(e.target.value)}
                size="small"
                sx={{ width: 100 }}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddZone}
                disabled={!newZoneName.trim() || isAddingZone}
                sx={{
                  backgroundColor: "#c4a574",
                  "&:hover": { backgroundColor: "#b39564" },
                  height: 40,
                }}
              >
                {t("common.add_zone") || "Ajouter"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Section Coûts */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #c4a574` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Euro sx={{ color: "#c4a574" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Coûts
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="cost_per_liter"
                control={control}
                rules={{
                  pattern: {
                    value: /^\d*\.?\d*$/,
                    message: t("common.number_only"),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="cost_per_liter"
                    label={t("common.cost_per_liter")}
                    fullWidth
                    type="number"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      inputProps: { step: "any" },
                    }}
                    placeholder={getPlaceholder("cost_per_liter")}
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.cost_per_liter[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="cost_per_kwh"
                control={control}
                rules={{
                  pattern: {
                    value: /^\d*\.?\d*$/,
                    message: t("common.number_only"),
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="cost_per_kwh"
                    label={t("common.cost_per_kwh")}
                    fullWidth
                    type="number"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      inputProps: { step: "any" },
                    }}
                    placeholder={getPlaceholder("cost_per_kwh")}
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.cost_per_kwh[error.type] : ""}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Section Options d'affichage */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #8a9ba8` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Settings sx={{ color: "#8a9ba8" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Options d'affichage
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {/* show_hide_screen is now always true and hidden */}
            <Controller
              name="show_hide_screen"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <input type="hidden" {...field} value={true} />
              )}
            />
            <Grid item xs={12} md={4}>
              <Controller
                name="show_hide_occupancy"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="show_hide_occupancy"
                    label={t("common.show_hide_occupancy")}
                    fullWidth
                    select
                    value={field.value === null ? "true" : field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value={"true"}>
                      {t("common.show", { name: t("common.occupancy") })}
                    </MenuItem>
                    <MenuItem value={"false"}>
                      {t("common.hide", { name: t("common.occupancy") })}
                    </MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="is_zone"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="is_zone"
                    label={t("common.is_zone")}
                    fullWidth
                    select
                    {...field}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value={true}>{t("common.yes")}</MenuItem>
                    <MenuItem value={false}>{t("common.no")}</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            {isZone && (
              <Grid item xs={12} md={4}>
                <Controller
                  name="zone_name"
                  control={control}
                  rules={{ required: isZone }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="zone_name"
                      label={t("common.zone_name")}
                      fullWidth
                      {...field}
                      error={error !== undefined}
                      helperText={error ? validationLabels.zone_name[error.type] : ""}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>

      {/* Section Paramètres écran */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #6b9dad` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Tv sx={{ color: "#6b9dad" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {t("common.screen")} - {t("common.settings")}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="screen_brightness"
                control={control}
                rules={{
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: 0,
                  max: 100,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="screen_brightness"
                    label={t("common.screen_brightness")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { step: 1 },
                    }}
                    placeholder={getPlaceholder("screen_brightness")}
                    {...field}
                    type="number"
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.screen_brightness[error.type] : ""}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="screen_on_start"
                control={control}
                rules={{
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: 0,
                  max: 23,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="screen_on_start"
                    label={t("common.screen_on_start")}
                    type="number"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">h</InputAdornment>,
                      inputProps: { step: 1 },
                    }}
                    placeholder={getPlaceholder("screen_on_start")}
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.screen_on_start[error.type] : ""}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="screen_on_end"
                control={control}
                rules={{
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: 0,
                  max: 23,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="screen_on_end"
                    label={t("common.screen_on_end")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">h</InputAdornment>,
                      inputProps: { step: 1 },
                    }}
                    placeholder={getPlaceholder("screen_on_end")}
                    type="number"
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.screen_on_end[error.type] : ""}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                name="day_start_hour"
                control={control}
                rules={{
                  pattern: { value: /^\d+$/, message: t("common.number_only") },
                  min: 0,
                  max: 23,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    id="day_start_hour"
                    label={t("common.day_start_hour") || "Début de journée"}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">h</InputAdornment>,
                      inputProps: { step: 1, min: 0, max: 23 },
                    }}
                    placeholder="0"
                    type="number"
                    {...field}
                    error={error !== undefined}
                    helperText={error ? error.message || validationLabels.day_start_hour[error.type] : "Pour les locations, mettre 12 (midi à midi)"}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            {userDB && userDB.admin && (
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="uplink_rate_seconds"
                  control={control}
                  rules={{
                    pattern: { value: /^\d+$/, message: t("common.number_only") },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="uplink_rate_seconds"
                      label={t("common.uplink_rate_seconds")}
                      fullWidth
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">{t("common.seconds")}</InputAdornment>,
                        inputProps: { step: 1 },
                      }}
                      placeholder={getPlaceholder("uplink_rate_seconds")}
                      {...field}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.uplink_rate_seconds[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>

      {/* Section Limites de consommation */}
      <Grid item xs={12}>
        <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #7a8a7a` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <WaterDrop sx={{ color: "#6b9dad" }} />
            <Bolt sx={{ color: "#c4a574" }} />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              {t("common.show_limits")}
            </Typography>
          </Box>

          {/* Limites Eau */}
          <Box sx={{ mb: 3 }}>
            <Chip
              icon={<WaterDrop sx={{ color: "#6b9dad !important" }} />}
              label={t("common.water_meter")}
              size="small"
              sx={{
                mb: 2,
                background: alpha("#6b9dad", 0.15),
                color: "#ffffff",
                "& .MuiChip-icon": { color: "#6b9dad" },
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="yellow_water_limit_per_person"
                  control={control}
                  rules={{
                    pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="yellow_water_limit_per_person"
                      label={t("common.yellow_water_limit_per_person")}
                      type="number"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">L</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("yellow_water_limit_per_person")}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      {...field}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.yellow_water_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="red_water_limit_per_person"
                  control={control}
                  rules={{
                    pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="red_water_limit_per_person"
                      label={t("common.red_water_limit_per_person")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">L</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("red_water_limit_per_person")}
                      type="number"
                      {...field}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.red_water_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Limites Électricité */}
          <Box>
            <Chip
              icon={<Bolt sx={{ color: "#c4a574 !important" }} />}
              label={t("common.electricity_meter")}
              size="small"
              sx={{
                mb: 2,
                background: alpha("#c4a574", 0.15),
                color: "#ffffff",
                "& .MuiChip-icon": { color: "#c4a574" },
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="yellow_elec_limit_per_person"
                  control={control}
                  rules={{
                    pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="yellow_elec_limit_per_person"
                      label={t("common.yellow_elec_limit_per_person")}
                      fullWidth
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("yellow_elec_limit_per_person")}
                      InputLabelProps={{ shrink: true }}
                      {...field}
                      type="number"
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.yellow_elec_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="red_elec_limit_per_person"
                  control={control}
                  rules={{
                    pattern: { value: /^\d*\.?\d*$/, message: t("common.number_only") },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      id="red_elec_limit_per_person"
                      label={t("common.red_elec_limit_per_person")}
                      fullWidth
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                        inputProps: { step: "any" },
                      }}
                      placeholder={getPlaceholder("red_elec_limit_per_person")}
                      {...field}
                      error={error !== undefined}
                      helperText={error ? error.message || validationLabels.red_elec_limit_per_person[error.type] : ""}
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>

      {/* Section Configuration Alertes SMS - Admin only */}
      {params.id && isAdmin && (
        <Grid item xs={12}>
          <Paper sx={{ ...sectionStyle, borderLeft: `4px solid #9a8878` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Phone sx={{ color: "#9a8878" }} />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Configuration des Alertes SMS
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Les contacts ci-dessous recevront un SMS en cas de dépassement
              des seuils critiques (RED) configurés dans les résidences.
            </Typography>

            {smsContacts.map((contact, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}
              >
                <TextField
                  label="Nom / Prénom"
                  value={contact.name}
                  onChange={(e) => handleSmsContactChange(index, 'name', e.target.value)}
                  sx={{ flex: 1 }}
                  size="small"
                />
                <TextField
                  label="Téléphone"
                  value={contact.phone}
                  onChange={(e) => handleSmsContactChange(index, 'phone', e.target.value)}
                  placeholder="+33612345678"
                  helperText="Format international requis"
                  sx={{ flex: 1 }}
                  size="small"
                />
                <IconButton
                  onClick={() => handleRemoveSmsContact(index)}
                  color="error"
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddSmsContact}
              sx={{ mt: 1, color: "#9a8878", borderColor: "#9a8878" }}
            >
              Ajouter un contact
            </Button>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.85rem', color: 'text.secondary' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Remarques importantes :
              </Typography>
              <ul style={{ marginTop: '8px', paddingLeft: '20px', margin: 0 }}>
                <li>Les numéros doivent être au format international (+33...)</li>
                <li>Coût approximatif : 0,04€ par SMS</li>
                <li>Pour activer les SMS sur une résidence, rendez-vous dans la page de modification de la résidence</li>
              </ul>
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Boutons d'action */}
      <Grid item xs={12}>
        <Stack
          spacing={2}
          direction="row"
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={() => navigate("/establishments/")}
            sx={{ color: "#8a9ba8", borderColor: "#8a9ba8" }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            sx={{
              background: "#78a882",
              "&:hover": { background: "#6a9a74" }
            }}
          >
            {initialEstablishment === null ? t("common.add") : t("common.update")}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(EstablishmentUpdate);
